import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import { API } from '../config';
import '../styles/theme.css';

const CHECK_META = {
  'Length':         { icon: 'fa-align-left',      color: 'blue' },
  'Contact Info':   { icon: 'fa-address-card',    color: 'orange' },
  'Sections':       { icon: 'fa-list-alt',         color: 'purple' },
  'Action Verbs':   { icon: 'fa-bolt',             color: 'orange' },
  'Quantification': { icon: 'fa-chart-line',       color: 'green' },
  'Skills':         { icon: 'fa-code',             color: 'blue' },
  'Education':      { icon: 'fa-graduation-cap',   color: 'purple' },
};

function ScoreRing({ score }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <svg width="140" height="140" viewBox="0 0 140 140">
      <circle cx="70" cy="70" r={r} fill="none" stroke="var(--border)" strokeWidth="12" />
      <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="12"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 70 70)"
        style={{ transition: 'stroke-dashoffset 1s ease' }} />
      <text x="70" y="65" textAnchor="middle" fill="var(--text-primary)"
        fontSize="28" fontWeight="700">{score}</text>
      <text x="70" y="85" textAnchor="middle" fill="var(--text-muted)" fontSize="12">/100</text>
    </svg>
  );
}

function CheckCard({ label, data }) {
  const meta = CHECK_META[label] || { icon: 'fa-check', color: 'blue' };
  const statusColor = { good: 'badge-green', fair: 'badge-orange', weak: 'badge-red' };
  const barColor = data.score >= 75 ? '#10b981' : data.score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="card" style={{ padding: '1.25rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div className={`stat-icon-wrap ${meta.color}`} style={{ flexShrink: 0 }}>
          <i className={`fas ${meta.icon}`}></i>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
            <span className={`badge ${statusColor[data.status] || 'badge-gray'}`}>
              {data.score}%
            </span>
          </div>
          <div style={{ marginTop: '0.4rem', height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${data.score}%`, background: barColor, borderRadius: 4, transition: 'width 0.8s ease' }} />
          </div>
        </div>
      </div>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>{data.message}</p>
      {data.missing?.length > 0 && (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
          {data.missing.map(s => (
            <span key={s} className="badge badge-red" style={{ fontSize: '0.72rem' }}>
              <i className="fas fa-times" style={{ marginRight: 3 }}></i>{s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CVOptimise() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const tier = localStorage.getItem('user_tier') || 'free';
  const isPremium = tier === 'premium';

  const analyse = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API}/cv-optimise/analyse`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Analysis failed');
      setData(json);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const scoreLabel = (s) => s >= 75 ? 'Strong' : s >= 50 ? 'Needs Work' : 'Weak';
  const scoreColor = (s) => s >= 75 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';

  const checkLabels = {
    length: 'Length', contact_info: 'Contact Info', sections: 'Sections',
    action_verbs: 'Action Verbs', quantification: 'Quantification',
    skills: 'Skills', education: 'Education',
  };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">CV Optimisation</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>
              AI CV Optimisation{' '}
              <span className="badge badge-purple" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>Premium</span>
            </h1>
            <p>Get a detailed score and actionable tips to improve your CV</p>
          </div>

          {!isPremium ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'block' }} />
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Premium Feature</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                AI CV Optimisation is available on the Premium plan (R599/month).
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                <i className="fas fa-crown"></i> Upgrade to Premium
              </button>
            </div>
          ) : (
            <>
              {/* Analyse button */}
              {!data && !loading && (
                <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                  <i className="fas fa-file-medical-alt" style={{ fontSize: '3rem', color: 'var(--primary)', marginBottom: '1rem', display: 'block' }} />
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Analyse Your CV</h3>
                  <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                    Our AI will score your CV across 7 key areas and give you specific tips to improve it.
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={analyse}>
                      <i className="fas fa-magic"></i> Analyse My CV
                    </button>
                    <button className="btn btn-secondary" onClick={() => navigate('/documents')}>
                      <i className="fas fa-upload"></i> Upload CV First
                    </button>
                  </div>
                </div>
              )}

              {loading && (
                <div className="empty-state">
                  <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
                  <p>Analysing your CV...</p>
                </div>
              )}

              {error && (
                <div className="alert alert-error">
                  <i className="fas fa-exclamation-circle"></i> {error}
                  {error.includes('upload') && (
                    <button className="btn btn-primary btn-sm" style={{ marginLeft: '1rem' }} onClick={() => navigate('/documents')}>
                      Upload CV
                    </button>
                  )}
                </div>
              )}

              {data && (
                <>
                  {/* Overall score */}
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                      <ScoreRing score={data.overall_score} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Overall CV Score</h2>
                          <span style={{
                            padding: '0.25rem 0.75rem', borderRadius: 99, fontSize: '0.875rem', fontWeight: 600,
                            background: scoreColor(data.overall_score) + '22', color: scoreColor(data.overall_score)
                          }}>
                            {scoreLabel(data.overall_score)}
                          </span>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                          Analysed: <strong style={{ color: 'var(--text-primary)' }}>{data.cv_filename}</strong>
                        </p>
                        {data.suggestions.length > 0 ? (
                          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                            <i className="fas fa-lightbulb" style={{ color: '#f59e0b', marginRight: 6 }}></i>
                            {data.suggestions.length} improvement{data.suggestions.length !== 1 ? 's' : ''} found.
                            {' '}Focus on the <strong>high priority</strong> items first.
                          </p>
                        ) : (
                          <p style={{ color: '#10b981', fontSize: '0.875rem', margin: 0 }}>
                            <i className="fas fa-check-circle" style={{ marginRight: 6 }}></i>
                            Your CV looks great across all areas!
                          </p>
                        )}
                        <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={analyse}>
                          <i className="fas fa-redo"></i> Re-analyse
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Priority suggestions */}
                  {data.suggestions.length > 0 && (
                    <div className="card" style={{ marginBottom: '1.5rem' }}>
                      <div className="card-header">
                        <span className="card-title">
                          <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b', marginRight: '0.5rem' }}></i>
                          Priority Improvements
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '0.5rem' }}>
                        {data.suggestions.map((s, i) => (
                          <div key={i} style={{
                            display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                            padding: '0.75rem 1rem', borderRadius: 8,
                            background: s.priority === 'high' ? 'rgba(239,68,68,0.07)' : 'rgba(245,158,11,0.07)',
                            border: `1px solid ${s.priority === 'high' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
                          }}>
                            <i className={`fas ${s.priority === 'high' ? 'fa-times-circle' : 'fa-exclamation-circle'}`}
                              style={{ color: s.priority === 'high' ? '#ef4444' : '#f59e0b', marginTop: 2, flexShrink: 0 }} />
                            <div>
                              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                                {s.category}
                              </span>
                              <span className={`badge ${s.priority === 'high' ? 'badge-red' : 'badge-orange'}`}
                                style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
                                {s.priority}
                              </span>
                              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                {s.message}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Detailed checks grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    {Object.entries(data.checks).map(([key, val]) => (
                      <CheckCard key={key} label={checkLabels[key] || key} data={val} />
                    ))}
                  </div>

                  {/* Missing keywords */}
                  {data.missing_keywords?.suggested?.length > 0 && (
                    <div className="card" style={{ marginBottom: '1.5rem' }}>
                      <div className="card-header">
                        <span className="card-title">
                          <i className="fas fa-tags" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                          Suggested Keywords to Add
                        </span>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                        {data.missing_keywords.message}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {data.missing_keywords.suggested.map(kw => (
                          <span key={kw} className="badge badge-blue" style={{ fontSize: '0.8rem' }}>
                            <i className="fas fa-plus" style={{ marginRight: 4 }}></i>{kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills found */}
                  {data.skills_found?.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <span className="card-title">
                          <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: '0.5rem' }}></i>
                          Skills Detected in Your CV
                        </span>
                        <span className="badge badge-green">{data.skills_found.length} skills</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.5rem' }}>
                        {data.skills_found.map(sk => (
                          <span key={sk} className="badge badge-gray" style={{ fontSize: '0.8rem' }}>{sk}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
